/**
 * Part 10 — VAT rounding / accounting precision for OMS → Xero.
 *
 * Xero calculates tax per line (2dp) then sums; suppliers often total then
 * tax (or round differently). A 1–2p difference is legitimate.
 *
 * Policy:
 * - Immaterial (≤ tolerance pennies): absorb via TaxAmount override / line adjust
 * - Minor (above tolerance, below material): absorb + warn (do not fail)
 * - Material: fail closed — do not silently absorb
 */

import { VAT_PERCENTAGE } from 'src/config'
import { OMS_VAT_TREATMENT } from 'src/utils/vatTreatment'

/** ± pennies treated as normal rounding noise (OMS vs Xero / supplier). */
export const VAT_ROUNDING_TOLERANCE_PENNIES = 2

/**
 * Absolute floor for "material" VAT mismatch (pennies).
 * Also compared to a % of expected VAT — see materialVatThresholdPennies.
 */
export const VAT_MATERIAL_DISCREPANCY_FLOOR_PENNIES = 10

/** Fraction of expected VAT that counts as material (e.g. 2%). */
export const VAT_MATERIAL_DISCREPANCY_RATE = 0.02

export const VAT_ROUNDING_STATUS = {
  EXACT: 'exact',
  IMMATERIAL: 'immaterial',
  MINOR: 'minor',
  MATERIAL: 'material',
}

export function roundMoneyPounds(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export function poundsToPennies(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100)
}

export function penniesToPounds(pennies) {
  return roundMoneyPounds(Number(pennies) / 100)
}

export function moneyPenniesDiff(aPounds, bPounds) {
  return Math.abs(poundsToPennies(aPounds) - poundsToPennies(bPounds))
}

/**
 * Xero-style line VAT: round(net * rate) to 2dp for Quantity=1, or
 * round(round(unit,2) * qty * rate) — we use net line amount * rate.
 */
export function calculateLineVatPounds(
  netLinePounds,
  { rate = VAT_PERCENTAGE, treatment = OMS_VAT_TREATMENT.STANDARD } = {},
) {
  if (treatment !== OMS_VAT_TREATMENT.STANDARD) return 0
  const net = roundMoneyPounds(netLinePounds)
  return roundMoneyPounds(net * rate)
}

export function materialVatThresholdPennies(expectedVatPennies) {
  const expected = Math.abs(Math.round(Number(expectedVatPennies) || 0))
  return Math.max(
    VAT_MATERIAL_DISCREPANCY_FLOOR_PENNIES,
    Math.round(expected * VAT_MATERIAL_DISCREPANCY_RATE),
  )
}

/**
 * Classify a VAT total difference (expected = supplier/document, actual = sum of lines).
 */
export function assessVatRoundingDiscrepancy({
  expectedVatPounds,
  actualVatPounds,
  tolerancePennies = VAT_ROUNDING_TOLERANCE_PENNIES,
} = {}) {
  const expectedPennies = poundsToPennies(expectedVatPounds)
  const actualPennies = poundsToPennies(actualVatPounds)
  const diffPennies = Math.abs(expectedPennies - actualPennies)
  const materialThreshold = materialVatThresholdPennies(expectedPennies)

  if (diffPennies === 0) {
    return {
      status: VAT_ROUNDING_STATUS.EXACT,
      diffPennies: 0,
      shouldFail: false,
      shouldAdjust: false,
      warn: false,
      expectedPennies,
      actualPennies,
      materialThreshold,
    }
  }

  if (diffPennies <= tolerancePennies) {
    return {
      status: VAT_ROUNDING_STATUS.IMMATERIAL,
      diffPennies,
      shouldFail: false,
      shouldAdjust: true,
      warn: false,
      expectedPennies,
      actualPennies,
      materialThreshold,
    }
  }

  if (diffPennies < materialThreshold) {
    return {
      status: VAT_ROUNDING_STATUS.MINOR,
      diffPennies,
      shouldFail: false,
      shouldAdjust: true,
      warn: true,
      expectedPennies,
      actualPennies,
      materialThreshold,
    }
  }

  return {
    status: VAT_ROUNDING_STATUS.MATERIAL,
    diffPennies,
    shouldFail: true,
    shouldAdjust: false,
    warn: true,
    expectedPennies,
    actualPennies,
    materialThreshold,
  }
}

/**
 * Adjust the largest standard-rated line's TaxAmount so sum(TaxAmount) matches
 * the supplier document VAT (within tolerance). Returns new lines + meta.
 *
 * @param {Array<{ TaxAmount?: number, TaxType?: string, UnitAmount?: number, Quantity?: number }>} lineItems
 * @param {number|null} documentVatPounds supplier invoice/CN VAT total
 */
export function reconcileLineTaxAmountsToDocumentVat(
  lineItems = [],
  documentVatPounds,
  options = {},
) {
  const lines = (Array.isArray(lineItems) ? lineItems : []).map(line => ({
    ...line,
    UnitAmount: roundMoneyPounds(line?.UnitAmount),
    TaxAmount:
      line?.TaxAmount == null
        ? undefined
        : roundMoneyPounds(line.TaxAmount),
  }))

  if (documentVatPounds == null || !Number.isFinite(Number(documentVatPounds))) {
    return {
      lineItems: lines,
      assessment: null,
      adjusted: false,
      documentVatPounds: null,
      lineVatSumPounds: sumLineTaxAmounts(lines),
    }
  }

  const targetVat = roundMoneyPounds(documentVatPounds)
  const lineVatSum = sumLineTaxAmounts(lines)
  const assessment = assessVatRoundingDiscrepancy({
    expectedVatPounds: targetVat,
    actualVatPounds: lineVatSum,
    tolerancePennies: options.tolerancePennies,
  })

  if (assessment.shouldFail) {
    const err = new Error(
      `Material VAT discrepancy of ${assessment.diffPennies}p between document VAT (£${targetVat.toFixed(
        2,
      )}) and line VAT sum (£${lineVatSum.toFixed(
        2,
      )}). Refusing to export — check the supplier document.`,
    )
    err.code = 'XERO_VAT_MATERIAL_DISCREPANCY'
    err.assessment = assessment
    throw err
  }

  if (!assessment.shouldAdjust || assessment.diffPennies === 0) {
    return {
      lineItems: lines,
      assessment,
      adjusted: false,
      documentVatPounds: targetVat,
      lineVatSumPounds: lineVatSum,
    }
  }

  // Prefer adjusting a standard-rated line (INPUT2 / has TaxAmount > 0).
  const adjustIndex = findVatAdjustmentLineIndex(lines)
  if (adjustIndex < 0) {
    // No line to adjust — keep supplier total via a synthetic note only if
    // single line; otherwise leave as-is for immaterial cases.
    return {
      lineItems: lines,
      assessment,
      adjusted: false,
      documentVatPounds: targetVat,
      lineVatSumPounds: lineVatSum,
    }
  }

  const deltaPounds = roundMoneyPounds(targetVat - lineVatSum)
  const next = lines.map((line, i) => {
    if (i !== adjustIndex) return line
    const current = roundMoneyPounds(line.TaxAmount || 0)
    const nextTax = roundMoneyPounds(current + deltaPounds)
    // TaxAmount must not be negative; clamp and re-check.
    return {
      ...line,
      TaxAmount: Math.max(0, nextTax),
    }
  })

  const afterSum = sumLineTaxAmounts(next)
  const after = assessVatRoundingDiscrepancy({
    expectedVatPounds: targetVat,
    actualVatPounds: afterSum,
    tolerancePennies: options.tolerancePennies,
  })

  if (after.shouldFail) {
    const err = new Error(
      `Material VAT discrepancy remains after rounding adjustment (${after.diffPennies}p).`,
    )
    err.code = 'XERO_VAT_MATERIAL_DISCREPANCY'
    err.assessment = after
    throw err
  }

  return {
    lineItems: next,
    assessment: after,
    adjusted: true,
    adjustmentPennies: poundsToPennies(deltaPounds),
    documentVatPounds: targetVat,
    lineVatSumPounds: afterSum,
  }
}

export function sumLineTaxAmounts(lineItems = []) {
  return roundMoneyPounds(
    (Array.isArray(lineItems) ? lineItems : []).reduce((sum, line) => {
      const tax = Number(line?.TaxAmount)
      return sum + (Number.isFinite(tax) ? tax : 0)
    }, 0),
  )
}

function findVatAdjustmentLineIndex(lines = []) {
  let best = -1
  let bestTax = -1
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]
    const tax = Number(line?.TaxAmount)
    const type = String(line?.TaxType || '').toUpperCase()
    const isStandard =
      type === 'INPUT2' ||
      type === 'INPUT' ||
      (Number.isFinite(tax) && tax > 0)
    if (!isStandard) continue
    if (tax >= bestTax) {
      bestTax = tax
      best = i
    }
  }
  if (best >= 0) return best
  // Fallback: last line with any TaxAmount field
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    if (lines[i]?.TaxAmount != null) return i
  }
  return lines.length ? lines.length - 1 : -1
}

/**
 * Compare two money amounts with penny tolerance (e.g. net match for full CN).
 */
export function moneyEqualsWithinTolerance(
  aPounds,
  bPounds,
  tolerancePennies = VAT_ROUNDING_TOLERANCE_PENNIES,
) {
  return moneyPenniesDiff(aPounds, bPounds) <= tolerancePennies
}

/**
 * Round document net/vat/gross consistently to 2dp.
 */
export function roundDocumentAmounts(amounts = {}) {
  if (!amounts) return amounts
  return {
    ...amounts,
    gross:
      amounts.gross == null ? null : roundMoneyPounds(amounts.gross),
    net: amounts.net == null ? null : roundMoneyPounds(amounts.net),
    vat: amounts.vat == null ? null : roundMoneyPounds(amounts.vat),
  }
}
